import { AccountService } from '../../../../services/account.service';
import { Component, OnDestroy } from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { Apollo, QueryRef } from 'apollo-angular';
import { merge, Observable, Subject, BehaviorSubject } from 'rxjs';
import { fromPromise } from 'rxjs/internal-compatibility';
import {
  exhaustMap,
  filter,
  first,
  ignoreElements,
  map,
  mapTo,
  switchMap,
  takeUntil,
  tap,
} from 'rxjs/operators';
import { apolloSimpleQueryUpdaterFactory } from '../../../../graphql/apollo-cache-utils';
import { Document, IAccount, ITodo } from '../../../../interfaces/graph-types';
import {
  refCountShareReplay,
  callOnSuccessOnError,
} from '../../../../rxjs-operators';
import { WatchAccountService } from '../../../../services/watch-account.service';
import * as gql from './account-todos-all.component.graphql';
import { NotificationCountersService } from '../../../../services/notification-counters.service';
import { Router, ActivatedRoute } from '@angular/router';

@Component({
  selector: '[account-todos-all]',
  template: require('./account-todos-all.component.pug'),
  styles: [require('./account-todos-all.component.css').toString()],
})
export class AccountTodosAllComponent implements OnDestroy {
  public readonly areTodosLoading$: Observable<boolean>;
  public readonly hasReachedEndOfTodos$ = new Subject<true>();
  public readonly todos$: Observable<ReadonlyArray<ITodo>>;

  public candidateSubjectId = new Subject<string>();
  public selectedTodo: BehaviorSubject<ITodo> = new BehaviorSubject<ITodo>(
    null,
  );

  public urlSafe: SafeResourceUrl;

  public chunks = [];

  public viewingCandidate: string;

  private bodyHtml: string = null;

  private readonly todosQueryRef: QueryRef<gql.TodosQuery, gql.TodosVariables> =
    this.apollo.watchQuery({
      query: gql.todosQuery(),
    });

  private todosEndCursor: string | undefined; // undefined for first load

  private readonly loadMoreTodosTrigger$ = new Subject<void>();

  emptySearchFilter = {
    types: ['Draft', 'Task', 'Reply', 'Call'],
  };
  searchFilter = this.emptySearchFilter;

  applicationStageClasses = {
    0: 'badge-secondary',
    1: 'badge-info',
    2: 'badge-info',
    3: 'badge-danger',
    4: 'badge-danger',
    5: 'badge-success',
    6: 'badge-warning',
    7: 'badge-secondary',
    8: 'badge-dark',
  };

  // -------------------------------------------------------------------
  public constructor(
    private readonly apollo: Apollo,
    private readonly watch: WatchAccountService,
    private readonly sanitizer: DomSanitizer,
    private notificationCountersService: NotificationCountersService,
    private router: Router,
    private route: ActivatedRoute,
  ) {
    const routeMap = this.route.snapshot.queryParamMap;
    // First check if route has params for filtering
    // Otherwise, we use the default filter (ie. show ALL types) and update the route accordingly
    if (routeMap.keys && routeMap.keys.length > 0) {
      routeMap.keys.forEach(key => {
        let value = routeMap['params'][key];
        if (key == 'types') {
          value = routeMap.getAll('types'); // use getAll because it returns an array of values even if there's only one
        }

        this.searchFilter[key] = value;
      });
    } else {
      this.updateRoute(this.searchFilter);
    }

    //=> Triggers first load, and then stays online for changes
    const todosQueryResult$ = this.watch.accountId().pipe(
      tap(
        accountId =>
          this.todosEndCursor ||
          this.todosQueryRef.setVariables({
            accountId,
            searchFilter: JSON.stringify(this.searchFilter),
          }),
      ),
      switchMap(() => this.todosQueryRef.valueChanges),
      tap(
        ({ data }) =>
          data && (this.todosEndCursor = data.account.todos.pageInfo.endCursor),
      ),
    );

    //=> Triggers when the user is reaching the end of the list, and loads more
    //   todos, updating the Apollo query cache, which will trigger valueChanges
    const loadMore$ = this.loadMoreTodosTrigger$.pipe(
      //=> Load more todos.. if that's not already running
      exhaustMap(() =>
        this.loadMoreTodos(
          this.todosEndCursor,
          JSON.stringify(this.searchFilter),
        ),
      ),
      //=> Is it the end of todos?
      tap(({ data }) => {
        if (data.account.todos.pageInfo.endCursor) return;
        this.hasReachedEndOfTodos$.next(true);
      }),
    );

    //=> Merge query results and "load more" observable.
    //   Just ignore its content, we only want it to be subscribed to along
    //   the query results.
    const todosQueryResultWithFetchMore$ = merge(
      todosQueryResult$,
      //=> Stop trying loading more results when we know we've reached the end
      loadMore$.pipe(takeUntil(this.hasReachedEndOfTodos$), ignoreElements()),
    ).pipe(refCountShareReplay());

    //=> Are todos currently loading? (first fetch or fetching more)
    this.areTodosLoading$ = todosQueryResultWithFetchMore$.pipe(
      map(({ loading }) => loading),
    );

    //=> Current list of todos
    this.todos$ = todosQueryResultWithFetchMore$.pipe(
      filter(({ data }) => !!data),
      map(
        ({ data }) => <ITodo[]>data.account.todos.edges.map(edge => edge.node),
      ),
    );
  }

  // -------------------------------------------------------------------
  public ngOnInit(): void {
    // Refresh counters
    this.notificationCountersService.refresh(this.watch.accountId().getValue());
  }

  // -------------------------------------------------------------------
  public ngOnDestroy(): void {}

  // -------------------------------------------------------------------
  public get accountId(): IAccount['id'] {
    return this.watch.accountId().getValue();
  }

  // -------------------------------------------------------------------
  public trackByDocumentId(index: number, doc: Document): Document['id'] {
    return doc.id; // unique id corresponding to the item
  }

  // -------------------------------------------------------------------
  public updateRoute(queryParams: any = null) {
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: queryParams,
    });
  }

  // -------------------------------------------------------------------
  public onTodoListScrollerScrolled(): void {
    this.loadMoreTodosTrigger$.next(void 0);
  }

  // -------------------------------------------------------------------
  public selectCandidateInfo(candidateId): void {
    this.viewingCandidate = candidateId;
    this.candidateSubjectId.next(candidateId);
  }

  // -------------------------------------------------------------------
  public closeCandidateInfo(): void {
    this.viewingCandidate = null;
  }

  // -------------------------------------------------------------------
  public selectTodo(todo): void {
    this.selectedTodo.next(todo);

    if (!todo) return;

    if (todo.candidate && todo.candidate.company) {
      this.urlSafe = this.sanitizer.bypassSecurityTrustResourceUrl(
        'https://' + todo.candidate.company.domain,
      );
    }
  }

  // -------------------------------------------------------------------
  public onCompleted(event) {
    // Completion happened in the child
    // this.notificationCountersService.refresh(this.watch.accountId().getValue());
    // const completedTodo = this.selectedTodo.getValue();
    // this.selectTodo(null);
    this.complete(
      this.selectedTodo,
      () => {},
      () => {},
    );
  }

  // -------------------------------------------------------------------
  public complete(
    data: unknown,
    onsuccess: onSmartSuccess,
    onfailure: onSmartFailure,
  ): void {
    const completedTodo = this.selectedTodo.getValue();
    this.apollo
      .mutate<gql.CompleteTodosMutation, gql.CompleteTodosVariables>({
        mutation: gql.completeTodosMutation(),
        variables: { todoIds: [completedTodo.id] },
        update: apolloSimpleQueryUpdaterFactory<
          gql.CompleteTodosMutation,
          gql.TodosQuery,
          gql.TodosVariables
        >({
          query: gql.todosQuery(),
          variables: {
            accountId: this.watch.accountId().getValue(),
            searchFilter: JSON.stringify(this.searchFilter),
          },
          update(data, queryData) {
            const filteredEdges = queryData.account.todos.edges.filter(
              edge => edge.node.id != completedTodo.id,
            );

            return {
              ...queryData,
              account: {
                ...queryData.account,
                todos: {
                  ...queryData.account.todos,
                  edges: filteredEdges,
                },
              },
            };
          },
        }),
      })
      .pipe(
        callOnSuccessOnError(
          () => {
            this.notificationCountersService.refresh(
              this.watch.accountId().getValue(),
            );
            onsuccess();
          },
          onfailure,
          ({ data }) => data['completeTodos']['error'],
        ),
        switchMap(() => this.selectNextTodo(completedTodo)),
      )
      .subscribe();
  }

  // -------------------------------------------------------------------
  public delete(
    data: unknown,
    onsuccess: onSmartSuccess,
    onfailure: onSmartFailure,
  ): void {
    const deletedTodo = this.selectedTodo.getValue();

    this.apollo
      .mutate<gql.DeleteTodosMutation, gql.DeleteTodosVariables>({
        mutation: gql.deleteTodosMutation(),
        variables: { todoIds: [deletedTodo.id] },
        update: apolloSimpleQueryUpdaterFactory<
          gql.DeleteTodosMutation,
          gql.TodosQuery,
          gql.TodosVariables
        >({
          query: gql.todosQuery(),
          variables: {
            accountId: this.watch.accountId().getValue(),
            searchFilter: JSON.stringify(this.searchFilter),
          },
          update(data, queryData) {
            const filteredEdges = queryData.account.todos.edges.filter(
              edge => edge.node.id != deletedTodo.id,
            );

            return {
              ...queryData,
              account: {
                ...queryData.account,
                todos: {
                  ...queryData.account.todos,
                  edges: filteredEdges,
                },
              },
            };
          },
        }),
      })
      .pipe(
        callOnSuccessOnError(
          () => {
            this.notificationCountersService.refresh(
              this.watch.accountId().getValue(),
            );
            onsuccess();
          },
          onfailure,
          ({ data }) => data['deleteTodos']['error'],
        ),
        switchMap(() => this.selectNextTodo(deletedTodo)),
      )
      .subscribe();
  }

  // -------------------------------------------------------------------
  private loadMoreTodos(after: string, searchFilter: string) {
    return fromPromise(
      this.todosQueryRef.fetchMore({
        query: gql.todosQuery(),
        variables: {
          accountId: this.watch.accountId().getValue(),
          after,
          searchFilter,
        },
        updateQuery(previous, { fetchMoreResult }) {
          if (!fetchMoreResult.account.todos.edges.length) return previous;

          return {
            ...fetchMoreResult,
            account: {
              ...fetchMoreResult.account,
              todos: {
                ...fetchMoreResult.account.todos,
                pageInfo: {
                  ...previous.account.todos.pageInfo,
                  endCursor: fetchMoreResult.account.todos.pageInfo.endCursor,
                },
                edges: [
                  ...previous.account.todos.edges,
                  ...fetchMoreResult.account.todos.edges,
                ],
              },
            },
          };
        },
      }),
    );
  }

  // -------------------------------------------------------------------
  private selectNextTodo(after: ITodo): Observable<void> {
    return this.todos$.pipe(
      first(),
      tap(todos => this.selectTodo(todos[todos.indexOf(after) + 1])),
      mapTo(void 0),
    );
  }

  // -------------------------------------------------------------------
  onTextChanged({ html, text, firstUpdate }) {
    this.bodyHtml = html;
  }

  // -------------------------------------------------------------------
  onUpdateFilter(filter = this.emptySearchFilter) {
    this.searchFilter = filter;
    this.updateRoute(this.searchFilter);

    this.todosQueryRef.setVariables({
      accountId: this.watch.accountId().getValue(),
      searchFilter: JSON.stringify(this.searchFilter),
    });
  }
}
